//client\src\coms\views\watch_together_parts\WatchTogetherPlayerPanel.js
import React, { useEffect, useRef } from 'react';
import { getProviderLabel, itemMediaKey, safeProvider } from './watchTogetherUtils';

export default function WatchTogetherPlayerPanel({
  activeItem,
  activeSkipVotes,
  majorityNeeded,
  playForEveryone,
  pauseForEveryone,
  toggleSkipVote,
  shareReady,
  identity,
  hasVotedToSkip,
  isShowingActiveLocally,
  iframeRef,
  mediaRef,
  spotifyMountRef,
  localEmbedUrl,
  localMediaKey,
  localProvider,
  onIframeLoad,
  onDirectMediaLoaded,
  onDirectMediaTimeUpdate,
  onDirectMediaEnded,
  setPlayerError,
  playerError,
  sharedPlayback,
  playerInfo,
  spotifyEmbedPrimed,
  spotifyPlayerActivationBusy,
  markSpotifyEmbedPrimed,
  watchTogetherSpeakerMuted,
  watchTogetherAudioStatus,
  canControlPlayback = true,
  controlMode = 'everyone',
  setControlMode,
  hostName = '',
  resyncToSharedPlayback,
  autoplayMuted = false,
  unmuteLocalPlayer,
  // Host or Manage sessions (watchPermissions.js); defaults to the old rule.
  canSwitchMode = undefined,
}) {
  const activeProvider = safeProvider(activeItem?.provider);
  const activeLabel = activeItem ? getProviderLabel(activeProvider) : 'Player';
  const playbackKey = itemMediaKey(activeItem);

  const canShowYouTube = !!(activeItem && activeProvider === 'youtube' && !!localEmbedUrl);
  const canShowSpotify = !!(activeItem && activeProvider === 'spotify');
  const miscMediaKind = String(activeItem?.mediaKind || '').trim().toLowerCase();
  const miscMediaType = String(activeItem?.mediaType || '').trim().toLowerCase();
  const isDirectMisc = !!(activeItem && activeProvider === 'misc' && (miscMediaKind === 'direct' || miscMediaType === 'video' || miscMediaType === 'audio'));
  const canShowMisc = !!(activeItem && activeProvider === 'misc' && !!localEmbedUrl);

  const showYouTubeSurface = !!(
    isShowingActiveLocally &&
    activeProvider === 'youtube' &&
    localProvider === 'youtube' &&
    !!localEmbedUrl
  );

  const showSpotifySurface = !!(
    isShowingActiveLocally &&
    activeProvider === 'spotify' &&
    localProvider === 'spotify'
  );

  const showDirectMiscSurface = !!(
    isShowingActiveLocally &&
    activeProvider === 'misc' &&
    localProvider === 'misc' &&
    isDirectMisc &&
    !!localEmbedUrl
  );

  const showIframeMiscSurface = !!(
    isShowingActiveLocally &&
    activeProvider === 'misc' &&
    localProvider === 'misc' &&
    !isDirectMisc &&
    !!localEmbedUrl
  );

  const isPlaying = !!(
    sharedPlayback &&
    sharedPlayback.playing &&
    localMediaKey &&
    playbackKey === localMediaKey
  );

  const duration = Number(playerInfo?.duration) || 0;
  const currentTime = Number(playerInfo?.currentTime) || 0;
  const remaining = duration > 0 ? Math.max(0, duration - currentTime) : 0;

  const lastDebugSnapshotRef = useRef('');
  const lastSurfaceRef = useRef('none');
  const handoverStartedAtRef = useRef(0);
  const handoverIdRef = useRef(0);

  const fmt = (value) => {
    const seconds = Math.max(0, Math.floor(Number(value) || 0));
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  const spotifyActionLabel = (() => {
    const mediaType = String(activeItem?.mediaType || '').trim().toLowerCase();
    if (mediaType === 'album') return 'Open album in Spotify';
    if (mediaType === 'playlist') return 'Open playlist in Spotify';
    if (mediaType === 'artist') return 'Open artist in Spotify';
    if (mediaType === 'show') return 'Open show in Spotify';
    if (mediaType === 'episode') return 'Open episode in Spotify';
    return 'Open track in Spotify';
  })();

  const showReadyOverlay = !!activeItem && !showYouTubeSurface && !showSpotifySurface && !showDirectMiscSurface && !showIframeMiscSurface;
  const showEmptyOverlay = !activeItem;

  const youtubeSurfaceStyle = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    opacity: showYouTubeSurface ? 1 : 0,
    visibility: showYouTubeSurface ? 'visible' : 'hidden',
    pointerEvents: showYouTubeSurface ? 'auto' : 'none',
    transition: 'opacity 160ms ease',
    background: '#000',
    zIndex: showYouTubeSurface ? 2 : 1,
  };

  const miscSurfaceStyle = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    opacity: showDirectMiscSurface || showIframeMiscSurface ? 1 : 0,
    visibility: showDirectMiscSurface || showIframeMiscSurface ? 'visible' : 'hidden',
    pointerEvents: showDirectMiscSurface || showIframeMiscSurface ? 'auto' : 'none',
    transition: 'opacity 160ms ease',
    background: '#000',
    zIndex: showDirectMiscSurface || showIframeMiscSurface ? 2 : 1,
  };

  const spotifySurfaceStyle = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    opacity: showSpotifySurface ? 1 : 0,
    visibility: showSpotifySurface ? 'visible' : 'hidden',
    pointerEvents: showSpotifySurface ? 'auto' : 'none',
    transition: 'opacity 160ms ease',
    background: 'var(--vxs-1, #121212)',
    zIndex: showSpotifySurface ? 2 : 1,
  };

  useEffect(() => {
    const spotifyMount = spotifyMountRef?.current || null;
    const iframe = iframeRef?.current || null;
    const spotifyChildCount = spotifyMount ? spotifyMount.childNodes.length : 0;
    const spotifyHasIframe = !!(
      spotifyMount &&
      (
        spotifyMount.querySelector('iframe') ||
        spotifyMount.querySelector('[title*="Spotify"]') ||
        spotifyMount.firstElementChild
      )
    );
    const iframeSrc = iframe?.src || '';
    const currentSurface = showYouTubeSurface ? 'youtube' : showSpotifySurface ? 'spotify' : showDirectMiscSurface ? 'misc-direct' : showIframeMiscSurface ? 'misc-iframe' : 'none';

    const snapshot = JSON.stringify({
      activeProvider,
      localProvider,
      isShowingActiveLocally: !!isShowingActiveLocally,
      canShowYouTube: !!canShowYouTube,
      canShowSpotify: !!canShowSpotify,
      canShowMisc: !!canShowMisc,
      showYouTubeSurface: !!showYouTubeSurface,
      showSpotifySurface: !!showSpotifySurface,
      showDirectMiscSurface: !!showDirectMiscSurface,
      showIframeMiscSurface: !!showIframeMiscSurface,
      localMediaKey: localMediaKey || '',
      playbackKey: playbackKey || '',
      localEmbedUrl: localEmbedUrl || '',
      spotifyEmbedPrimed: !!spotifyEmbedPrimed,
      spotifyPlayerActivationBusy: !!spotifyPlayerActivationBusy,
      spotifyChildCount,
      spotifyHasIframe,
      iframeSrc,
    });

    if (lastDebugSnapshotRef.current !== snapshot) {
      console.log('[WatchTogether][Handover][State]', {
        activeProvider,
        localProvider,
        isShowingActiveLocally,
        canShowYouTube,
        canShowSpotify,
        canShowMisc,
        showYouTubeSurface,
        showSpotifySurface,
        showDirectMiscSurface,
        showIframeMiscSurface,
        localMediaKey,
        playbackKey,
        localEmbedUrl,
        spotifyEmbedPrimed,
        spotifyPlayerActivationBusy,
        spotifyChildCount,
        spotifyHasIframe,
        iframeSrc,
      });
      lastDebugSnapshotRef.current = snapshot;
    }

    if (lastSurfaceRef.current === 'youtube' && currentSurface === 'spotify') {
      handoverStartedAtRef.current = Date.now();
      handoverIdRef.current += 1;
      console.log('[WatchTogether][Handover] YouTube -> Spotify surface switch started', {
        handoverId: handoverIdRef.current,
        activeProvider,
        localProvider,
        localMediaKey,
        playbackKey,
        spotifyChildCount,
        spotifyHasIframe,
      });
    }

    if (
      handoverStartedAtRef.current &&
      currentSurface === 'spotify' &&
      spotifyHasIframe
    ) {
      console.log('[WatchTogether][Handover] Spotify surface has mounted content', {
        handoverId: handoverIdRef.current,
        elapsedMs: Date.now() - handoverStartedAtRef.current,
        spotifyChildCount,
        spotifyHasIframe,
        localMediaKey,
        playbackKey,
      });
      handoverStartedAtRef.current = 0;
    }

    lastSurfaceRef.current = currentSurface;
  }, [
    activeProvider,
    canShowMisc,
    canShowSpotify,
    canShowYouTube,
    iframeRef,
    isShowingActiveLocally,
    localEmbedUrl,
    localMediaKey,
    localProvider,
    playbackKey,
    showSpotifySurface,
    showDirectMiscSurface,
    showIframeMiscSurface,
    showYouTubeSurface,
    spotifyEmbedPrimed,
    spotifyMountRef,
    spotifyPlayerActivationBusy,
  ]);

  useEffect(() => {
    const spotifyMount = spotifyMountRef?.current;
    if (!spotifyMount) return undefined;

    const observer = new MutationObserver(() => {
      const childCount = spotifyMount.childNodes.length;
      const spotifyHasIframe = !!(
        spotifyMount.querySelector('iframe') ||
        spotifyMount.querySelector('[title*="Spotify"]') ||
        spotifyMount.firstElementChild
      );

      console.log('[WatchTogether][Handover][SpotifyMountMutation]', {
        activeProvider,
        localProvider,
        showSpotifySurface,
        childCount,
        spotifyHasIframe,
        htmlPreview: String(spotifyMount.innerHTML || '').slice(0, 220),
      });

      if (handoverStartedAtRef.current && showSpotifySurface && spotifyHasIframe) {
        console.log('[WatchTogether][Handover] Spotify mount mutation completed handover', {
          handoverId: handoverIdRef.current,
          elapsedMs: Date.now() - handoverStartedAtRef.current,
          childCount,
        });
        handoverStartedAtRef.current = 0;
      }
    });

    observer.observe(spotifyMount, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    return () => observer.disconnect();
  }, [activeProvider, localProvider, showSpotifySurface, spotifyMountRef]);

  const handleYouTubeLoad = () => {
    console.log('[WatchTogether][Handover][YouTubeIframeLoad]', {
      activeProvider,
      localProvider,
      showYouTubeSurface,
      localMediaKey,
      playbackKey,
      src: iframeRef?.current?.src || localEmbedUrl || '',
    });

    try {
      onIframeLoad();
    } catch {}
  };

  return (
    <div
      style={{
        border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))',
        background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.03 * var(--vx-ov, 1)))',
        borderRadius: 16,
        padding: 14,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', flexShrink: 0 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 900 }}>Player</div>
          <div style={{ marginTop: 4, fontSize: 12, opacity: 0.72 }}>
            {activeItem ? `Active ${activeLabel.toLowerCase()} item: ${activeItem.mediaId}` : 'No active queue item yet.'}
          </div>
          {activeItem ? (
            <div style={{ marginTop: 6, fontSize: 12, opacity: 0.72 }}>
              Skip votes: {activeSkipVotes.length}/{majorityNeeded}
            </div>
          ) : null}
          {autoplayMuted && typeof unmuteLocalPlayer === 'function' ? (
            <button
              type="button"
              onClick={unmuteLocalPlayer}
              style={{ marginTop: 6, padding: '6px 10px', borderRadius: 8, border: '1px solid rgba(250,204,21,0.6)', background: 'rgba(250,204,21,0.15)', color: 'var(--vx-text, #fff)', fontWeight: 800, fontSize: 12, cursor: 'pointer' }}
            >
              🔇 Playing muted (your browser blocked sound). Click to unmute
            </button>
          ) : null}
          {shareReady ? (
            <div style={{ marginTop: 6, fontSize: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ opacity: 0.72 }}>
                {controlMode === 'host'
                  ? `Host controls playback${hostName ? ` (${hostName})` : ''}`
                  : 'Everyone can control playback'}
              </span>
              {(canSwitchMode === undefined ? canControlPlayback : canSwitchMode) && typeof setControlMode === 'function' ? (
                <button
                  type="button"
                  onClick={() => setControlMode(controlMode === 'host' ? 'everyone' : 'host')}
                  style={{
                    border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.14 * var(--vx-ov, 1)))',
                    background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))',
                    color: 'var(--vx-text, #fff)',
                    borderRadius: 8,
                    padding: '2px 8px',
                    fontSize: 11,
                    fontWeight: 800,
                    cursor: 'pointer',
                  }}
                >
                  {controlMode === 'host' ? 'Let everyone control' : 'Host only'}
                </button>
              ) : null}
            </div>
          ) : null}
          {activeProvider === 'spotify' && activeItem ? (
            <div style={{ marginTop: 6, fontSize: 12, opacity: 0.8, maxWidth: 620, lineHeight: 1.45 }}>
              Spotify items should swap straight into the embed with no extra activation step.
            </div>
          ) : null}
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={playForEveryone}
            disabled={!activeItem || !shareReady || !canControlPlayback}
            style={{
              height: 38,
              borderRadius: 12,
              padding: '0 14px',
              border: '1px solid rgba(var(--vx-accent-rgb, 99,102,241), 0.42)',
              background: activeItem && shareReady && canControlPlayback ? 'rgba(var(--vx-accent-rgb, 88,101,242), 0.18)' : 'rgba(var(--vx-accent-rgb, 88,101,242), 0.08)',
              color: 'var(--vx-text, #fff)',
              cursor: activeItem && shareReady && canControlPlayback ? 'pointer' : 'not-allowed',
              fontWeight: 900,
              fontSize: 13,
              opacity: activeItem && shareReady && canControlPlayback ? 1 : 0.6,
            }}
          >
            Play for all
          </button>

          <button
            type="button"
            onClick={pauseForEveryone}
            disabled={!activeItem || !shareReady || !canControlPlayback}
            style={{
              height: 38,
              borderRadius: 12,
              padding: '0 14px',
              border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
              background: activeItem && shareReady && canControlPlayback ? 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))' : 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.02 * var(--vx-ov, 1)))',
              color: 'var(--vx-text, #fff)',
              cursor: activeItem && shareReady && canControlPlayback ? 'pointer' : 'not-allowed',
              fontWeight: 800,
              fontSize: 13,
              opacity: activeItem && shareReady && canControlPlayback ? 1 : 0.55,
            }}
          >
            Pause for all
          </button>

          {typeof resyncToSharedPlayback === 'function' && activeItem && activeProvider !== 'spotify' ? (
            <button
              type="button"
              onClick={resyncToSharedPlayback}
              title="Jump to the shared playback position"
              style={{
                height: 38,
                borderRadius: 12,
                padding: '0 14px',
                border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
                background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))',
                color: 'var(--vx-text, #fff)',
                cursor: 'pointer',
                fontWeight: 800,
                fontSize: 13,
              }}
            >
              Re-sync
            </button>
          ) : null}

          <button
            type="button"
            onClick={toggleSkipVote}
            disabled={!activeItem || !shareReady || !identity}
            style={{
              height: 38,
              borderRadius: 12,
              padding: '0 14px',
              border: hasVotedToSkip ? '1px solid rgba(245,158,11,0.58)' : '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
              background: hasVotedToSkip ? 'rgba(245,158,11,0.18)' : 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))',
              color: 'var(--vx-text, #fff)',
              cursor: activeItem && shareReady && identity ? 'pointer' : 'not-allowed',
              fontWeight: 900,
              fontSize: 13,
              opacity: activeItem && shareReady && identity ? 1 : 0.55,
            }}
          >
            {hasVotedToSkip ? 'Undo skip vote' : 'Skip to next'}
          </button>

          {activeProvider === 'spotify' && activeItem?.shareUrl ? (
            <a
              href={activeItem.shareUrl}
              target="_blank"
              rel="noreferrer"
              style={{
                height: 38,
                borderRadius: 12,
                padding: '0 14px',
                border: '1px solid rgba(29,185,84,0.55)',
                background: 'rgba(29,185,84,0.24)',
                color: 'var(--vx-text, #fff)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                textDecoration: 'none',
                cursor: 'pointer',
                fontWeight: 900,
                fontSize: 13,
              }}
            >
              {spotifyActionLabel}
            </a>
          ) : null}
        </div>
      </div>

      <div
        style={{
          position: 'relative',
          width: '100%',
          flex: 1,
          minHeight: 0,
          borderRadius: 14,
          overflow: 'hidden',
          background: 'rgba(0,0,0,0.55)',
          border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))',
        }}
      >
        <div style={youtubeSurfaceStyle}>
          <iframe
            key={canShowYouTube ? localEmbedUrl : 'watch-together-youtube-idle'}
            ref={iframeRef}
            src={canShowYouTube ? localEmbedUrl : 'about:blank'}
            title={`watch-together-${localMediaKey || 'idle'}`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              border: '0',
              background: '#000',
            }}
            onLoad={canShowYouTube ? handleYouTubeLoad : undefined}
            onError={() => setPlayerError('Could not load the YouTube video here.')}
          />
        </div>


        <div style={miscSurfaceStyle}>
          {showDirectMiscSurface ? (
            miscMediaType === 'audio' ? (
              <audio
                ref={mediaRef}
                src={localEmbedUrl}
                controls
                preload="metadata"
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
                onLoadedMetadata={onDirectMediaLoaded}
                onCanPlay={onDirectMediaLoaded}
                onTimeUpdate={onDirectMediaTimeUpdate}
                onPlay={onDirectMediaTimeUpdate}
                onPause={onDirectMediaTimeUpdate}
                onEnded={onDirectMediaEnded}
                onError={() => setPlayerError('Could not load that direct audio link here.')}
              />
            ) : (
              <video
                ref={mediaRef}
                src={localEmbedUrl}
                controls
                playsInline
                preload="metadata"
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', background: '#000' }}
                onLoadedMetadata={onDirectMediaLoaded}
                onCanPlay={onDirectMediaLoaded}
                onTimeUpdate={onDirectMediaTimeUpdate}
                onPlay={onDirectMediaTimeUpdate}
                onPause={onDirectMediaTimeUpdate}
                onEnded={onDirectMediaEnded}
                onError={() => setPlayerError('Could not load that direct video link here.')}
              />
            )
          ) : null}

          {showIframeMiscSurface ? (
            <iframe
              key={canShowMisc ? localEmbedUrl : 'watch-together-misc-idle'}
              src={canShowMisc ? localEmbedUrl : 'about:blank'}
              title={`watch-together-misc-${localMediaKey || 'idle'}`}
              allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: '0', background: '#000' }}
              onLoad={() => setPlayerError('')}
              onError={() => setPlayerError('Could not load that misc embed here.')}
            />
          ) : null}
        </div>

        <div style={spotifySurfaceStyle}>
          <div
            ref={spotifyMountRef}
            style={{
              position: 'absolute',
              inset: 0,
            }}
          />

          <div
            style={{
              position: 'absolute',
              right: 12,
              bottom: 12,
              borderRadius: 999,
              padding: '6px 10px',
              fontSize: 11,
              fontWeight: 900,
              background: 'rgba(var(--vxs-1-rgb, 18,18,18), 0.88)',
              border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.14 * var(--vx-ov, 1)))',
              color: 'var(--vx-text, #fff)',
              pointerEvents: 'none',
            }}
          >
            {watchTogetherSpeakerMuted ? 'Spotify muted by Voxemera' : isPlaying ? 'Spotify playing' : 'Spotify paused'}
          </div>
        </div>

        {showReadyOverlay ? (
          <>
            <img
              src={activeItem.thumbnailUrl}
              alt=""
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                filter: 'brightness(0.5)',
              }}
            />
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                padding: 24,
                background: 'linear-gradient(180deg, rgba(var(--vxs-2-rgb, 20,20,24), 0.30), rgba(var(--vxs-0-rgb, 10,10,14), 0.74))',
                zIndex: 3,
              }}
            >
              <div>
                <div style={{ fontSize: 22, fontWeight: 900, letterSpacing: '-0.02em' }}>
                  Ready for watch together
                </div>
                <div style={{ marginTop: 8, fontSize: 13, opacity: 0.82, maxWidth: 560 }}>
                  {activeProvider === 'spotify'
                    ? 'The active Spotify item will appear here as an embed.'
                    : activeProvider === 'misc'
                      ? 'Misc items share the same queue. Direct media can play here, and embeddable hosts can open in-frame here.'
                      : 'Use play for all to start the active video for everybody in the room.'}
                </div>
                <div style={{ marginTop: 8, fontSize: 12, opacity: 0.78 }}>
                  Skip votes: {activeSkipVotes.length}/{majorityNeeded}
                </div>
                <div style={{ marginTop: 14, display: 'flex', justifyContent: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={playForEveryone}
                    disabled={!shareReady || !canControlPlayback}
                    style={{
                      height: 46,
                      borderRadius: 14,
                      padding: '0 18px',
                      border: '1px solid rgba(52,211,153,0.38)',
                      background: shareReady && canControlPlayback ? 'rgba(16,185,129,0.18)' : 'rgba(16,185,129,0.08)',
                      color: 'var(--vx-text, #fff)',
                      cursor: shareReady && canControlPlayback ? 'pointer' : 'not-allowed',
                      fontWeight: 900,
                      fontSize: 14,
                      opacity: shareReady && canControlPlayback ? 1 : 0.6,
                    }}
                  >
                    Play for all
                  </button>

                  <button
                    type="button"
                    onClick={pauseForEveryone}
                    disabled={!shareReady || !canControlPlayback}
                    style={{
                      height: 46,
                      borderRadius: 14,
                      padding: '0 18px',
                      border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
                      background: shareReady && canControlPlayback ? 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))' : 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.02 * var(--vx-ov, 1)))',
                      color: 'var(--vx-text, #fff)',
                      cursor: shareReady && canControlPlayback ? 'pointer' : 'not-allowed',
                      fontWeight: 800,
                      fontSize: 14,
                      opacity: shareReady && canControlPlayback ? 1 : 0.6,
                    }}
                  >
                    Pause for all
                  </button>

                  <button
                    type="button"
                    onClick={toggleSkipVote}
                    disabled={!shareReady || !identity}
                    style={{
                      height: 46,
                      borderRadius: 14,
                      padding: '0 18px',
                      border: hasVotedToSkip ? '1px solid rgba(245,158,11,0.58)' : '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
                      background: hasVotedToSkip ? 'rgba(245,158,11,0.18)' : 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))',
                      color: 'var(--vx-text, #fff)',
                      cursor: shareReady && identity ? 'pointer' : 'not-allowed',
                      fontWeight: 900,
                      fontSize: 14,
                      opacity: shareReady && identity ? 1 : 0.6,
                    }}
                  >
                    {hasVotedToSkip ? 'Undo skip vote' : 'Skip to next'}
                  </button>

                  {activeProvider === 'spotify' && activeItem?.shareUrl ? (
                    <a
                      href={activeItem.shareUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        height: 46,
                        borderRadius: 14,
                        padding: '0 18px',
                        border: '1px solid rgba(29,185,84,0.55)',
                        background: 'rgba(29,185,84,0.24)',
                        color: 'var(--vx-text, #fff)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textDecoration: 'none',
                        fontWeight: 900,
                        fontSize: 14,
                      }}
                    >
                      {spotifyActionLabel}
                    </a>
                  ) : null}
                </div>
              </div>
            </div>
          </>
        ) : null}

        {showEmptyOverlay ? (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              padding: 24,
              background: 'linear-gradient(180deg, rgba(var(--vxs-2-rgb, 20,20,24), 0.72), rgba(var(--vxs-0-rgb, 10,10,14), 0.90))',
              zIndex: 3,
            }}
          >
            <div>
              <div style={{ fontSize: 20, fontWeight: 900, letterSpacing: '-0.02em' }}>
                Shared queue player
              </div>
              <div style={{ marginTop: 8, fontSize: 13, opacity: 0.74, maxWidth: 520 }}>
                Once the queue arrives, the active queue item can be shown here.
              </div>

              {watchTogetherAudioStatus ? (
                <div style={{ marginTop: 8, fontSize: 12, opacity: 0.76 }}>
                  {watchTogetherAudioStatus}
                </div>
              ) : null}

              {playerError ? (
                <div style={{ marginTop: 8, fontSize: 12, color: '#ffb4b4', fontWeight: 700 }}>
                  {playerError}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      {activeItem && activeProvider !== 'spotify' && duration > 0 ? (
        <div style={{ fontSize: 12, opacity: 0.76, flexShrink: 0 }}>
          {isPlaying ? 'Playing' : 'Paused'} · {fmt(currentTime)} / {fmt(duration)} · {fmt(remaining)} remaining
        </div>
      ) : null}

      {watchTogetherAudioStatus ? (
        <div style={{ fontSize: 12, opacity: 0.76, flexShrink: 0 }}>
          {watchTogetherAudioStatus}
        </div>
      ) : null}

      {playerError ? (
        <div style={{ fontSize: 12, color: '#ffb4b4', fontWeight: 700, flexShrink: 0 }}>
          {playerError}
        </div>
      ) : null}
    </div>
  );
}